(() => {
  if (customElements.get('mrw-cart-drawer')) return;
  class MrwCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.busy = false;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-cart-close]') || event.target === this.dialog) this.close();
        const toggle = event.target.closest('[data-protection-toggle]');
        if (toggle) this.toggleProtection(toggle);
        const change = event.target.closest('[data-cart-change]');
        if (change) this.change(change);
        if (event.target.closest('[data-cart-retry]')) this.refresh();
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-protection-schedule]')) this.syncProtection();
      });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('transitionend', event => {
        if (event.target === this.dialog && event.propertyName === 'transform' && this.dialog.dataset.visible === 'false') this.finishClose();
      });
      this.dialog.addEventListener('close', () => {
        clearTimeout(this.closeTimer);
        cancelAnimationFrame(this.openFrame);
        this.dialog.dataset.visible = 'false';
        document.body.style.overflow = this.previousOverflow || '';
        if (this.trigger?.isConnected) this.trigger.focus({preventScroll: true});
      });
      this.addEventListener('submit', event => {
        if (event.target.matches('[data-cart-checkout]') && (this.busy || this.protectionMismatch)) event.preventDefault();
      });
      this.sync();
    }
    get sectionId() { return this.dataset.sectionId; }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('[data-cart-change], [data-protection-toggle], [name="checkout"]').forEach(button => { button.disabled = value; });
      this.syncProtection();
    }
    open(trigger) {
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.openFrame);
      if (!this.dialog.open) {
        this.trigger = trigger || document.activeElement;
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.dialog.dataset.visible = 'false';
        this.dialog.showModal();
        this.dialog.getBoundingClientRect();
      }
      this.openFrame = requestAnimationFrame(() => { this.dialog.dataset.visible = 'true'; });
    }
    close() {
      if (!this.dialog.open) return;
      cancelAnimationFrame(this.openFrame);
      clearTimeout(this.closeTimer);
      this.dialog.dataset.visible = 'false';
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.finishClose();
      else this.closeTimer = setTimeout(() => this.finishClose(), 300);
    }
    finishClose() {
      if (this.dialog.dataset.visible === 'true') return;
      clearTimeout(this.closeTimer);
      this.dialog.close();
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.openFrame);
      if (this.dialog?.open) {
        this.dialog.close();
        document.body.style.overflow = this.previousOverflow || '';
      }
    }
    sync() {
      const content = this.querySelector('[data-cart-render]');
      const count = Number(content.dataset.count || 0);
      document.querySelectorAll('[data-mrw-cart-count]').forEach(node => { node.textContent = count; node.hidden = count === 0; });
      this.syncProtection();
      if (typeof window.bucksCC?.reConvert === 'function') window.bucksCC.reConvert();
    }
    render(html) {
      const source = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-cart-render]');
      if (!source) throw new Error(this.dataset.error);
      const active = document.activeElement;
      const key = active?.closest('[data-cart-key]')?.dataset.cartKey;
      const action = active?.dataset.cartAction;
      this.querySelector('[data-cart-render]').replaceWith(source);
      this.sync();
      if (key && action) {
        const row = [...this.querySelectorAll('[data-cart-key]')].find(node => node.dataset.cartKey === key);
        const target = row?.querySelector('[data-cart-action="' + action + '"]');
        (target || this.querySelector('[data-cart-close]')).focus();
      }
    }
    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      error.querySelector('[data-cart-error-text]').textContent = message || this.dataset.error;
      error.hidden = false;
    }
    state() { return this.querySelector('[data-cart-render]'); }
    protectionLines() { return Array.from(this.state().querySelectorAll('[data-protection-line]')); }
    protectionCadence(config) {
      try {
        const {billing,delivery} = JSON.parse(config);
        const n = Number(billing.frequency);
        if ([30,60,90].includes(n) && billing.interval === 'DAY' && delivery.interval === 'DAY' &&
            Number(delivery.frequency) === n && billing.maxCycles == null && (billing.minCycles == null || Number(billing.minCycles) <= 1)) return n;
      } catch {}
      return null;
    }
    protectionChoices() {
      const state = this.state(), choices = new Map();
      if (!Number(state.dataset.count) || state.dataset.protectionMode !== 'paid' || state.dataset.serviceOk !== 'true') return choices;
      const subscriptions = [...state.querySelectorAll('[data-cart-key][data-subscription]')];
      if (!subscriptions.length) {choices.set('once',''); return choices;}
      const wanted = new Set(subscriptions.map(row => this.protectionCadence(row.dataset.config)).filter(Boolean));
      for (const opt of state.querySelectorAll('[data-protection-plan]')) {
        const days = this.protectionCadence(opt.dataset.config);
        if (wanted.has(days) && !choices.has(String(days))) choices.set(String(days),opt.dataset.protectionPlan);
      }
      return choices;
    }
    syncProtection() {
      const state = this.state();
      if (!state) return;
      const paid = state.dataset.protectionMode === 'paid', choices = this.protectionChoices(), valid = new Set(choices.values());
      const lines = this.protectionLines(), active = lines.find(l=>l.dataset.variant === state.dataset.protectionId && valid.has(l.dataset.plan || ''));
      this.protectionMismatch = lines.length > 0 && (lines.length !== 1 || !active || active.dataset.quantity !== '1');
      const checkout = state.querySelector('[name="checkout"]');
      if (checkout) checkout.disabled = this.busy || this.protectionMismatch;
      const row = state.querySelector('[data-protection-row]'), button = state.querySelector('[data-protection-toggle]');
      if (!paid || !row || !button) return;
      row.hidden = !choices.size;
      const select = row.querySelector('[data-protection-schedule]'), frequency = row.querySelector('[data-protection-frequency]');
      if (!select || !frequency) return;
      const activeKey = active && [...choices].find(([,id])=>id === (active.dataset.plan || ''))?.[0];
      const key = activeKey || (choices.size === 1 ? choices.keys().next().value : choices.has(select.value) ? select.value : '');
      for (const opt of select.options) {if (opt.value) opt.hidden = opt.disabled = !choices.has(opt.value);}
      select.hidden = choices.size <= 1;
      select.disabled = this.busy || Boolean(active);
      select.value = key === 'once' ? '' : key || '';
      frequency.textContent = key && key !== 'once' ? ' · ' + [...select.options].find(o=>o.value===key)?.textContent : '';
      button.dataset.requestedPlan = choices.get(key) || '';
      button.setAttribute('aria-checked',String(Boolean(active)));
      button.disabled = this.busy || !choices.has(key) || this.protectionMismatch;
    }
    async readSection() {
      const url = new URL(this.dataset.cartUrl, location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url.href, {headers:{Accept:'text/html'},cache:'no-store'});
      if (!response.ok) throw new Error(this.dataset.error);
      this.render(await response.text());
      if (this.busy) this.setBusy(true);
    }
    async postCart(url, payload) {
      const response = await fetch(url, {method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({...payload,sections:[this.sectionId],sections_url:location.pathname})});
      const result = await response.json();
      if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || this.dataset.error);
      if (result.sections?.[this.sectionId]) this.render(result.sections[this.sectionId]);
      else await this.readSection();
      if (this.busy) this.setBusy(true);
    }
    async reconcileProtection() {
      const state = this.state().dataset, lines = this.protectionLines(), updates = {};
      const valid = new Set(this.protectionChoices().values());
      let preference = state.protectionPref || '', kept = false;
      for (const line of lines) {
        const keep = state.count !== '0' && state.protectionMode === 'paid' && state.serviceOk === 'true' && line.dataset.variant === state.protectionId && valid.has(line.dataset.plan || '') && !kept;
        if (!keep || line.dataset.quantity !== '1') updates[line.dataset.protectionLine] = keep ? 1 : 0;
        if (keep) kept = true;
      }
      if (!Number(state.count) || state.protectionMode !== 'included') preference = '';
      else preference = preference === 'Declined' ? 'Declined' : 'Included';
      const payload = {};
      if (Object.keys(updates).length) payload.updates = updates;
      if (preference !== (state.protectionPref || '')) payload.attributes = {'Shipping protection':preference};
      if (Object.keys(payload).length) await this.postCart(this.dataset.updateUrl,payload);
    }
    async toggleProtection(button) {
      if (this.busy || button.disabled || this.protectionMismatch) return;
      const state = this.state().dataset, lines = this.protectionLines(), enable = button.getAttribute('aria-checked') !== 'true';
      this.setBusy(true); this.querySelector('[data-cart-error]').hidden = true;
      try {
        if (state.protectionMode === 'included') {
          await this.postCart(this.dataset.updateUrl,{attributes:{'Shipping protection':enable ? 'Included' : 'Declined'}});
        } else if (state.protectionMode === 'paid' && state.serviceOk === 'true' && Number(state.count)) {
          if (enable && !lines.length) {
            const plan = button.dataset.requestedPlan || '';
            if (![...this.protectionChoices().values()].includes(plan)) return;
            const item = {id:state.protectionId,quantity:1,properties:{_mrw_cart_extra:'shipping_protection'}};
            if (plan) item.selling_plan = plan;
            await this.postCart(this.dataset.addUrl,{items:[item]});
            await this.reconcileProtection();
            if (!this.protectionLines().some(line=>line.dataset.variant===state.protectionId && (line.dataset.plan || '')===plan)) throw new Error(this.dataset.error);
          }
          else if (!enable && lines.length) await this.postCart(this.dataset.updateUrl,{updates:Object.fromEntries(lines.map(l=>[l.dataset.protectionLine,0]))});
        }
      } catch (error) {
        try {await this.readSection();} catch {}
        this.showError(error.message);
      } finally {this.setBusy(false); if(this.dialog.open) this.querySelector('[data-protection-toggle]')?.focus({preventScroll:true});}
    }
    async refresh() {
      if (this.busy) return;
      this.setBusy(true); this.querySelector('[data-cart-error]').hidden = true;
      try {await this.readSection(); await this.reconcileProtection();}
      catch (error) {this.showError(error.message);}
      finally {this.setBusy(false);}
    }
    async afterAdd(result, trigger) {
      this.open(trigger); this.setBusy(true); this.querySelector('[data-cart-error]').hidden = true;
      try {
        if (result.sections?.[this.sectionId]) {this.render(result.sections[this.sectionId]); this.setBusy(true);}
        else await this.readSection();
        await this.reconcileProtection();
      } catch(error) {this.showError(error.message);}
      finally {this.setBusy(false);}
    }
    async change(button) {
      if (this.busy) return;
      const row = button.closest('[data-cart-key]');
      const quantity = Number(button.dataset.cartChange);
      if (!row || !Number.isInteger(quantity) || quantity < 0) return;
      this.setBusy(true);
      this.querySelector('[data-cart-error]').hidden = true;
      try {
        if (quantity === 0 && Number(this.state().dataset.count) === Number(row.dataset.quantity)) {
          const updates = Object.fromEntries(this.protectionLines().map(l=>[l.dataset.protectionLine,0]));
          updates[row.dataset.cartKey] = 0;
          await this.postCart(this.dataset.updateUrl,{updates,attributes:{'Shipping protection':''}});
        } else {
          await this.postCart(this.dataset.changeUrl,{id:row.dataset.cartKey,quantity});
          await this.reconcileProtection();
        }
      } catch (error) {
        try {await this.readSection();} catch {}
        this.showError(error.message);
      } finally { this.setBusy(false); }
    }
  }
  customElements.define('mrw-cart-drawer', MrwCartDrawer);
  const drawer = () => document.querySelector('mrw-cart-drawer');
  window.MrwCart = {
    requestData() { return drawer() ? {sections: [drawer().sectionId], sections_url: location.pathname} : {}; },
    async afterAdd(result, trigger) {
      if (drawer()) return drawer().afterAdd(result, trigger);
      location.assign((window.Shopify?.routes?.root || '/') + 'cart');
    },
    open(trigger) { if (drawer()) { drawer().open(trigger); return drawer().refresh(); } }
  };
  document.addEventListener('click', event => {
    const link = event.target.closest('a[data-mrw-cart-open], .wf-icons a, #cart-icon-bubble');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !drawer()) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== drawer().dataset.cartUrl.replace(/\/$/, '')) return;
    event.preventDefault();
    window.MrwCart.open(link);
  });
})();
